<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  canManageDirectory: { type: Boolean, default: false },
  canCreateContact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
  'newConversation',
]);
</script>

<template>
  <header class="contacts-page-header sticky top-0 z-10 px-3 sm:px-6">
    <div
      class="contacts-page-header__inner flex flex-col items-stretch sm:flex-row sm:items-center justify-between w-full py-4 gap-3 sm:gap-4 mx-auto max-w-5xl"
    >
      <span class="min-w-0 truncate text-[28px] font-bold text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div
        class="flex w-full items-stretch flex-col sm:w-auto sm:items-center sm:flex-row flex-shrink-0 gap-2 sm:gap-4"
      >
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'contacts-page-header__search-input !h-11 !min-h-11 !rounded-full !border-0 !bg-[#f5f7fb] ltr:!pl-10 rtl:!pr-10 !text-sm',
            ]"
            class="contacts-page-header__search-field group min-h-11 w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-10 size-4 top-1/2 ltr:left-3 rtl:right-3 z-10"
              />
            </template>
          </Input>
        </div>
        <div
          class="flex w-full items-center justify-between sm:w-auto sm:justify-start flex-shrink-0 gap-2 sm:gap-4"
        >
          <div
            class="acl-mobile-account-menu-host flex min-w-0 items-center gap-1 sm:gap-2"
          >
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              :can-manage-directory="canManageDirectory"
              :can-create-contact="canCreateContact"
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
          <div class="hidden sm:block w-px h-4 bg-n-strong" />
          <!-- Aceleres: usa o mesmo fluxo do Chat de Atendimento (painel de
               contato/telefone + seleção de instância), montado no wrapper. -->
          <Button
            :label="buttonLabel"
            size="sm"
            @click="emit('newConversation')"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.contacts-page-header {
  background: #f6f6f6;
  box-shadow: 0 1px 0 rgba(226, 232, 240, 0.9);
  isolation: isolate;
}

.contacts-page-header::before {
  content: '';
  position: absolute;
  inset: 0 -1.5rem;
  z-index: -1;
  background: #f6f6f6;
}

.contacts-page-header__inner {
  box-sizing: border-box;
}

.contacts-page-header__search-field :deep(.contacts-page-header__search-input) {
  height: 44px !important;
  min-height: 44px !important;
  line-height: 44px !important;
  background: rgba(194, 189, 184, 0.15) !important;
}
</style>
